import styles from "./style.module.css";

import logo from "components/header/EvergreenLogo.png";
import { Cover } from "./cover";
import { fetchJson } from "shared/fetch/fetchJson";
import { getQueryStringValueWithType } from "shared/url/getQueryStringValue";
import { useAbortController } from "shared/useAbortController";
import { SiteSurvey } from "api/types/siteSurvey";
import { SiteSurveyConfiguration } from "api/types/siteSurveyConfiguration";
import { useRunOnMount } from "shared/useRunOnMount/useRunOnMount";
import { useToast } from "shared/toast/useToast";
import { useTranslation } from "shared/i18n/useTranslation";
import { Fragment, useState } from "react";
import Loading from "shared/loading";
import { CenteredContainer } from "shared/centeredContainer";
import { Comments } from "./comments";
import { SitePhots } from "./sitePhotos";
import { UnitsTable } from "./unitsTable";
import { UnitSummary } from "./unitSummary";

import { UploadImageButton } from "components/uploadButton";
import { sprint1ParseInt } from "shared/parseInt";

import { Photos } from "./photos";
import { getUnitTitle, getUnitPhotoCaptionPairs } from "./helpers";
import { SiteSummary } from "./siteSummary";

export function ViewReport() {
  const { survey, config } = useData();
  const printInput = usePrintInput();

  if (!survey || !config) {
    return (
      <CenteredContainer>
        <Loading />
      </CenteredContainer>
    );
  }

  return (
    <div className={`container ${styles.container}`}>
      <PrintOptions {...printInput} />
      <Container survey={survey}>
        <Cover
          survey={survey}
          coverUrl={printInput.cover}
          logoUrl={printInput.logo}
          rating={printInput.rating}
        />
      </Container>

      {survey.comments && (
        <Container survey={survey}>
          <Comments commentText={survey.comments} />
        </Container>
      )}

      {survey.attributes?.length > 0 && (
        <Container survey={survey}>
          <SiteSummary config={config} siteSurvey={survey} />
        </Container>
      )}

      {survey.photos?.length > 0 && (
        <Container survey={survey}>
          <SitePhots photos={survey.photos} config={config} />
        </Container>
      )}
      {survey.units?.length > 0 && (
        <>
          <Container survey={survey}>
            <UnitsTable config={config} units={survey.units} />
          </Container>

          {survey.units.map((unit, index) => {
            const combined = getUnitPhotoCaptionPairs(unit, config);

            return (
              <Fragment key={`unit-${index}-${unit.id}`}>
                <Container survey={survey}>
                  <UnitSummary config={config} unit={unit} />
                </Container>
                {combined?.length > 0 && (
                  <Container survey={survey}>
                    <Photos
                      title={getUnitTitle(unit)}
                      photoAndCaptionPairs={combined}
                    />
                  </Container>
                )}
              </Fragment>
            );
          })}
        </>
      )}
    </div>
  );
}

function usePrintInput() {
  const [logo, setLogo] = useState(undefined);
  const [cover, setCover] = useState(undefined);
  const [rating, setRating] = useState<number | undefined>(undefined);

  function handlePrint() {
    window.print();
  }

  function handleLogoUpload(tempUrl) {
    setLogo(tempUrl);
  }
  function handleCoverUpload(tempUrl) {
    setCover(tempUrl);
  }

  function handleRatingChange(e: React.ChangeEvent<HTMLSelectElement>) {
    e.preventDefault();
    const ratingValue = sprint1ParseInt(e.target.value);
    setRating(ratingValue);
  }

  return {
    rating,
    logo,
    handleLogoUpload,
    cover,
    handleCoverUpload,
    handleRatingChange,
    handlePrint,
  };
}

function PrintOptions({
  handleLogoUpload,
  handleCoverUpload,
  handleRatingChange,
  rating,
  handlePrint,
}) {
  return (
    <div className="d-print-none border-bottom">
      <div className="p-3">
        <UploadImageButton
          label="Upload Company Logo"
          onFileUpload={handleLogoUpload}
        />

        <br />

        <UploadImageButton
          labelClassName="mt-2"
          label="Upload Cover Page Photo"
          onFileUpload={handleCoverUpload}
        />

        <br />
        <select
          className="form-select mt-2"
          style={{ width: "30%" }}
          value={rating}
          onChange={handleRatingChange}
        >
          <option>Equipment Rating (none)</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
        </select>

        <br />
        <button className="btn btn-primary" onClick={handlePrint}>
          Print
        </button>
      </div>
    </div>
  );
}

function Container({
  survey,
  children,
}: {
  survey: SiteSurvey;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`${styles.pageGap}  ${styles.pageBreak} ${styles.pageContainer} mx-auto`}
    >
      <div className="d-flex flex-column  h-100">
        <Header survey={survey} />
        <div className="h-100">{children}</div>
        {/* <Footer /> */}
      </div>
    </div>
  );
}

function Header({ survey }: { survey: SiteSurvey }) {
  return (
    <div className="print-header">
      <div className="d-flex justify-content-between align-items-center border-bottom border-dark">
        <img src={logo} alt="logo" />
        <div className="text-end px-2">
          <div className="fs-3">HVAC Inspection</div>
          {survey.modifiedOn && (
            <div className="">
              Inspected On: {new Date(survey.modifiedOn).toDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type FetchDataReturnType = {
  error?: string;
  config?: SiteSurveyConfiguration;
  survey?: SiteSurvey;
};

async function fetchData(signal: AbortSignal): Promise<FetchDataReturnType> {
  const surveyId = getQueryStringValueWithType("id");
  if (!surveyId) {
    return { error: "__invalidUrl" };
  }
  const configPromise = fetchJson<SiteSurveyConfiguration>(
    "/api/SiteSurveyConfiguration/Get",
    { signal }
  );
  const surveyPromise = fetchJson<SiteSurvey>(
    `/api/siteSurvey/get?siteSurveyId=${surveyId}`,
    { signal }
  );
  try {
    const [{ data: config }, { data: survey }] = await Promise.all([
      configPromise,
      surveyPromise,
    ]);
    return { config, survey };
  } catch (e) {
    return { error: "__SorrySomethingBadHappenedTryAgain" };
  }
}

function useData() {
  const abortController = useAbortController();
  const toast = useToast();
  const [config, setConfig] = useState<SiteSurveyConfiguration>();
  const [survey, setSurvey] = useState<SiteSurvey>();

  const { translate } = useTranslation();
  useRunOnMount(() => {
    async function load() {
      const { error, survey, config } = await fetchData(abortController.signal);
      if (survey && config) {
        setConfig(config);
        setSurvey(survey);
      } else {
        toast.error(translate(error ?? "__SorrySomethingBadHappenedTryAgain"));
      }
    }

    load();
  });

  return { survey, config };
}
